<template>
  <div class="bg-white rounded-md p-4">
    <!-- Create Organization Section -->
    <div class="bg-[#2DA7D71F] w-full border border-dashed border-[#2DA7D7] p-4 mb-4">
      <div class="text-xl font-medium mb-4 flex items-center">
        <Layers class="h-6 w-6 mr-2 text-[#2DA7D7]" />
        {{ $t("Create Your Own Organization") }}
      </div>
      <div class="flex items-center">
        <TextInput
          :auto-focus="true"
          :value="organization_name"
          :validate-on-input="true"
          :disabled="loading"
          label=""
          :classes="'w-full'"
          name="organization_name"
          type="text"
          :placeholder="$t('Organization name')"
          @input="organization_name = $event.target.value" />

        <button
          :class="{ 'opacity-60 pointer-events-none': !organization_name_available || loading || !organization_name }"
          class="btn feature-btn rounded-md w-fit ml-2 flex items-center justify-center gap-1 min-w-24"
          @click="createOrganization">
          <Plus v-if="!loading" class="h-4 w-4" />
          <Loader v-if="loading" class="mr-1 animate-spin h-4 w-4 text-white border-2 border-white rounded-full" />
          {{ loading ? $t("Creating...") : $t("Create") }}
        </button>
      </div>

      <div v-if="name_taken" class="text-red-500 text-sm mt-2 flex items-center">
        <AlertCircleIcon class="h-4 w-4 mr-1" />
        {{ $t("Organization name already taken") }}
      </div>

      <div v-if="professional_domain" class="flex items-center text-sm mt-4 bg-white p-3 rounded-md shadow-sm border border-gray-100">
        <input
          id="allow-join"
          v-model="allow_others_to_join"
          :disabled="loading"
          type="checkbox"
          name="truncate"
          class="form-checkbox text-[#2DA7D7] focus:ring-[#2DA7D7] h-5 w-5 rounded transition duration-150 ease-in-out">

        <label for="allow-join" class="ml-2 block text-sm leading-5 text-gray-700">
          {{ $t("Allow others with") }} <span class="font-medium">@{{ $store.state.auth.user.email?.split("@")[1] }}</span>
          {{ $t("domain to auto-join my organization") }}
        </label>
      </div>
    </div>

    <!-- Organizations List Section -->
    <div v-if="user_organizations.length > 0" class="mb-6 border rounded-md p-4 bg-gray-50">
      <div class="text-xl font-medium mb-4 flex items-center">
        <Building2 class="h-6 w-6 mr-2 text-gray-500" />
        {{ $t("Your Organizations") }}
      </div>

      <div class="overflow-scroll max-h-60">
        <div
          v-for="org in user_organizations"
          :key="org.id"
          class="mb-3 bg-white border rounded-md flex items-center py-3 px-4 hover:shadow-md transition-shadow duration-200">
          <Avatar
            :image="org.website ? `https://logosnap.io/api/${org.website}?token=ecoTB81m5dQqkrjw` : null"
            :name="org.name"
            :email="org.website"
            :radius="38"
            :is-company="true" />

          <div class="ml-3">
            <div class="font-medium text-gray-900">
              {{ org.name }}
            </div>
            <div class="text-xs text-gray-500">
              {{ org.is_owner ? $t("Owner") : $t("Member") }} · {{ org.users_count }} {{ $t(org.users_count === 1 ? "member" : "members") }}
            </div>
          </div>

          <div class="ml-auto flex gap-2">
            <button
              v-if="!org.is_current"
              class="btn btn-sm bg-[#2DA7D7] hover:bg-[#2DA7D7]/90 text-white rounded-md px-3 py-1 text-sm"
              @click="switchToOrganization(org)">
              {{ $t("Switch") }}
            </button>
            <button
              v-if="!org.is_owner"
              class="btn btn-sm border border-red-500 text-red-500 hover:bg-red-50 rounded-md px-3 py-1 text-sm"
              @click="confirmLeaveOrganization(org)">
              {{ $t("Leave") }}
            </button>
            <button
              v-if="org.is_owner && !org.is_current"
              class="btn btn-sm border border-red-500 text-red-500 hover:bg-red-50 rounded-md px-3 py-1 text-sm"
              @click="confirmDeleteOrganization(org)">
              {{ $t("Delete") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Organizations Section -->
    <div v-if="professional_domain">
      <div class="text-xl font-medium mb-4 flex items-center">
        <Building2 class="h-6 w-6 mr-2 text-gray-500" />
        {{ !organizations.length ? $t("No") : "" }} {{ $t("Organizations with") }}
        <span class="text-[#2DA7D7] px-1">@{{ $store.state.auth.user.email?.split("@")[1] }}</span>
        {{ $t("domain") }}
      </div>

      <div v-if="organizations.length > 0" class="overflow-scroll max-h-80">
        <div v-for="organization in organizations" :key="organization.id" class="mb-3">
          <div class="border rounded-md flex items-center py-3 px-4 hover:shadow-sm transition-shadow duration-200">
            <Avatar
              :image="organization.website ? `https://logosnap.io/api/${organization.website}?token=ecoTB81m5dQqkrjw` : null"
              :name="organization.name"
              :email="organization.website"
              :radius="38"
              :is-company="true" />

            <div class="ml-3">
              <div class="font-medium">
                {{ organization.name }}
              </div>
              <div class="text-xs text-gray-500">
                {{ organization.users_count || "0" }}
                {{ $t(organization.users_count === 1 || !organization.users_count ? "member" : "members") }}
              </div>
            </div>

            <div class="flex ml-auto gap-2">
              <button
                class="btn btn-sm rounded-md px-3 py-1 text-sm"
                :class="
                  organization.allow_users_to_join
                    ? 'bg-[#0D8ABA] hover:bg-[#0D8ABA]/90 text-white'
                    : organization.requested
                      ? 'border border-blue-500 text-blue-500 hover:bg-blue-50'
                      : 'border border-[#0D8ABA] text-[#0D8ABA] hover:bg-blue-50'
                "
                @click="joinOrganization(organization)">
                {{ organization.allow_users_to_join ? $t("Join") : organization.requested ? $t("Resend Request") : $t("Request to Join") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="flex flex-col items-center justify-center py-8 text-center bg-gray-50 rounded-md border border-dashed border-gray-300">
        <Building2 class="h-12 w-12 text-gray-400 mb-3" stroke-width="1" />
        <p class="text-gray-600 mb-1">
          {{ $t("No organizations found with your domain") }}
        </p>
        <p class="text-gray-500 text-sm">
          {{ $t("Create your own organization to get started") }}
        </p>
      </div>
    </div>

    <!-- Confirmation modal for delete/leave organization -->
    <div v-if="show_confirmation_modal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div class="text-xl font-medium mb-4">
          {{ confirmation_title }}
        </div>
        <p class="text-gray-600 mb-6">
          {{ confirmation_message }}
        </p>
        <div class="flex justify-end gap-3">
          <button class="btn cancel-btn px-4 py-2" @click="show_confirmation_modal = false">
            {{ $t("Cancel") }}
          </button>
          <button class="btn bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md" @click="confirmAction">
            {{ confirmation_action_text }}
          </button>
        </div>
      </div>
    </div>

    <div class="flex gap-2 p-2 mt-6 justify-end">
      <button type="button" class="cancel-btn px-6 py-2" @click="$emit('cancel')">{{ $t("Close") }}</button>
    </div>
  </div>
</template>

<script>
import TextInput from "@/components/shared/TextInput.vue"
import Avatar from "@/components/shared/Avatar.vue"
import dataService from "@/services/data.service"
import { Layers, Plus, Loader, AlertCircleIcon, Building2 } from "lucide-vue-next"

export default {
  components: { Avatar, TextInput, Layers, Plus, Loader, AlertCircleIcon, Building2 },

  emits: ["cancel"],
  data() {
    return {
      allow_others_to_join: false,
      organization_name: "",
      organization_name_available: false,
      name_taken: false,
      loading: false,
      organizations: [],
      user_organizations: [],
      check_name_timeout: null,

      // Confirmation modal properties
      show_confirmation_modal: false,
      confirmation_title: "",
      confirmation_message: "",
      confirmation_action_text: "",
      pendingAction: null,
      pending_action_data: null
    }
  },

  computed: {
    professional_domain() {
      const domain = this.$store.state.auth.user?.email?.split("@")[1]

      let professional_domain_calc = true
      const domains = [
        "gmail.com",
        "yahoo.com",
        "outlook.com",
        "hotmail.com",
        "aol.com",
        "icloud.com",
        "mail.com",
        "protonmail.com",
        "zoho.com",
        "yandex.com",
        "gmx.com",
        "yahoo.co.uk",
        "live.com",
        "me.com",
        "qq.com",
        "yopmail.com",
        "ymail.com",
        "msn.com"
      ]
      if (domains.includes(domain)) {
        professional_domain_calc = false
      }
      return professional_domain_calc
    },

    current_organization_id() {
      return this.$store.state.organization?.id
    },
    current_user_id() {
      return this.$store.state.auth.user?.id
    }
  },

  watch: {
    organization_name() {
      clearTimeout(this.check_name_timeout)
      this.check_name_timeout = setTimeout(() => {
        if (this.organization_name.trim()) {
          this.checkOrganizationName()
        } else {
          this.organization_name_available = false
          this.name_taken = false
        }
      }, 600)
    }
  },

  created() {
    this.getUserOrganizations()
  },

  methods: {
    getUserOrganizations() {
      dataService
        .getOrganizations()
        .then((res) => {
          let orgs = res.data.map((org) => ({
            ...org,
            is_owner: org.owner === this.current_user_id,
            is_current: org.id === this.current_organization_id,
            users_count: org.users?.length || 0,
            requested: org.join_requests && org.join_requests.some((req) => req.user_id === this.current_user_id)
          }))
          this.organizations = orgs.filter((org) => !org.users.some((u) => u.id === this.current_user_id))
          this.user_organizations = orgs.filter((org) => org.users.some((u) => u.id === this.current_user_id))
        })
        .catch((error) => {
          console.error("Error fetching user organizations:", error)
        })
    },

    switchToOrganization(org) {
      this.loading = true
      this.$store.commit("setOrganization", org)
      window.location.href = "/"
    },

    confirmLeaveOrganization(org) {
      this.confirmation_title = this.$t("Leave Organization")
      this.confirmation_message = this.$t(
        `Are you sure you want to leave '${org.name}'? You will no longer have access to this organization's resources.`
      )
      this.confirmation_action_text = this.$t("Leave")
      this.pendingAction = this.leaveOrganization
      this.pending_action_data = org
      this.show_confirmation_modal = true
    },

    leaveOrganization(org) {
      this.loading = true
      dataService
        .leaveOrganization(org.id)
        .then(() => {
          // Remove the organization from user_organizations
          this.user_organizations = this.user_organizations.filter((o) => o.id !== org.id)
          this.loading = false

          // If this was the current organization, redirect to onboarding
          if (org.id === this.current_organization_id) {
            window.location.href = "/onboarding"
          }
        })
        .catch((error) => {
          console.error("Error leaving organization:", error)
          this.loading = false
        })
    },

    confirmDeleteOrganization(org) {
      this.confirmation_title = this.$t("Delete Organization")
      this.confirmation_message = this.$t(
        `Are you sure you want to delete '${org.name}'? This action cannot be undone and all data will be permanently lost.`
      )
      this.confirmation_action_text = this.$t("Delete")
      this.pendingAction = this.deleteOrganization
      this.pending_action_data = org
      this.show_confirmation_modal = true
    },

    deleteOrganization(org) {
      this.loading = true
      dataService
        .deleteOrganization({org_id:org.id})
        .then(() => {
          // Remove the organization from user_organizations
          this.user_organizations = this.user_organizations.filter((o) => o.id !== org.id)
          this.loading = false

          // If this was the current organization, redirect to onboarding
          if (org.id === this.current_organization_id) {
            window.location.href = "/onboarding"
          }
        })
        .catch((error) => {
          console.error("Error deleting organization:", error)
          this.loading = false
        })
    },

    confirmAction() {
      if (this.pendingAction && this.pending_action_data) {
        this.pendingAction(this.pending_action_data)
        this.show_confirmation_modal = false
        this.pendingAction = null
        this.pending_action_data = null
      }
    },
    joinOrganization(org) {
      this.loading = true
      dataService
        .joinOrganization(org.id)
        .then((res) => {
          this.loading = false
          if (res.data.status == "ORGANIZATION_JOINED") {
            if (org.all) {
              this.switchToOrganization(org)
            }
          } else if (res.data.status == "ORGANIZATION_JOIN_REQUEST_SENT") {
            this.getUserOrganizations()
          }
        })
        .catch((error) => {
          console.error("Error joining organization:", error)
          this.loading = false
        })
    },

    checkOrganizationName() {
      dataService
        .checkOrganizationName(this.organization_name)
        .then((res) => {
          if (res.data.status == "ORGANIZATION_NAME_NOT_USED") {
            this.organization_name_available = true
            this.name_taken = false
          } else {
            this.organization_name_available = false
            this.name_taken = true
          }
        })
        .catch((error) => {
          console.error("Error checking organization name:", error)
          this.organization_name_available = false
        })
    },
    createOrganization() {
      this.loading = true
      dataService
        .createOrganization({
          name: this.organization_name,
          allow_users_to_join: this.allow_others_to_join
        })
        .then((res) => {
          this.organization_name = ""
          this.organization_name_available = false
          this.name_taken = false
          // Navigate to the new organization
          this.switchToOrganization(res.data)
        })
        .catch((error) => {
          console.error("Error creating organization:", error)
          this.loading = false
        })
    }
  }
}
</script>
